import React from "react"
import { twMerge } from "tailwind-merge"
import { CommandItem } from "../base_ui"
import type { SearchRowProps } from "./types"

// The shared shell for every result row: a cmdk CommandItem (keyboard nav via aria-activedescendant,
// correct focus/tab handling) wrapping a real <a href> when the hit is addressable. The anchor gives us
// semantics + right-click / modifier-click "open in new tab"; its onClick stops propagation so cmdk's
// own item click doesn't ALSO fire (double selection), then reports selection WITH the DOM event so the
// connected handler can preventDefault + fire analytics. cmdk's onSelect covers keyboard Enter, which
// carries no event. (asChild is intentionally avoided: cmdk's onSelect passes the item value, not an
// event, and Slot-merging cmdk's click with the anchor's would double-fire.)
export const ResultRowShell: React.FC<SearchRowProps & { children: React.ReactNode }> = ({
  hit,
  position,
  className,
  onSelect,
  children,
}) => {
  const select = (event?: React.MouseEvent) => onSelect(event, { hit, position })
  const contentClass = twMerge("flex w-full items-center gap-3 px-4 py-2", className)

  return (
    <CommandItem
      // A stable, unique value per result so cmdk can track the active item (filtering is disabled).
      value={`${hit.__typename}:${hit.id ?? hit.path}`}
      // Keyboard Enter on the active item — no DOM event; the handler navigates without one.
      onSelect={() => select()}
      className={hit.path ? "p-0" : contentClass}
    >
      {hit.path ? (
        <a
          href={hit.path}
          // Not a tab stop: cmdk drives keyboard nav from the input. The href is for semantics +
          // right-click / modifier-click open-in-new-tab; plain clicks are intercepted below.
          tabIndex={-1}
          onClick={(event) => {
            // Keep the click from reaching cmdk's item handler, so selection fires exactly once.
            event.stopPropagation()
            select(event)
          }}
          className={contentClass}
        >
          {children}
        </a>
      ) : (
        children
      )}
    </CommandItem>
  )
}
